import { useCallback, useEffect, useState } from 'react';
import { IconChevronDown, IconChevronUp, IconCopy, IconPlus, IconTrash } from '@tabler/icons-react';
import { Button, Input, cn, toast } from '@heroui/react';
import type { Page } from '@/components/canvas/document/publicTypes';
import {
  canNavigateWorkspacePage,
  navigateWorkspacePage,
} from '@/components/canvas/page/navigateWorkspacePage';
import { pageChromeNavIconClass } from '@/components/canvas/page/pageChromeNavIcons';
import { MAX_WORKSPACE_PAGES } from '@/components/canvas/workspace/viewport/workspaceLimits';
import {
  PAGE_CHROME_HEIGHT,
  PAGE_CHROME_MIN_SCREEN_WIDTH,
  pageChromeWorldHeight,
} from '@/components/canvas/workspace/model/pageArrangement';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';

export { PAGE_CHROME_HEIGHT };

export interface PageChromeProps {
  page: Page;
  pageIndex: number;
  pageCount: number;
  pageWidth: number;
  zoom: number;
  isActive: boolean;
  isDragging: boolean;
  onDragHandleMouseDown: (e: React.MouseEvent) => void;
  onChromeClick: (e: React.MouseEvent) => void;
  readonly?: boolean;
}

export function PageChrome({
  page,
  pageIndex,
  pageCount,
  pageWidth,
  zoom,
  isActive,
  isDragging,
  onDragHandleMouseDown,
  onChromeClick,
  readonly = false,
}: PageChromeProps) {
  const deletePage = useCanvasStore((s) => s.deletePage);
  const duplicatePage = useCanvasStore((s) => s.duplicatePage);
  const addPageAfter = useCanvasStore((s) => s.addPageAfter);
  const setPageName = useCanvasStore((s) => s.setPageName);
  const setCurrentPageId = useCanvasStore((s) => s.setCurrentPageId);
  const workspacePageLayoutMode = useCanvasStore((s) => s.workspacePageLayoutMode);

  const [nameDraft, setNameDraft] = useState(page.name);

  useEffect(() => {
    setNameDraft(page.name);
  }, [page.id, page.name]);

  const safeZoom = zoom > 0 ? zoom : 1;
  const minWorldWidth = PAGE_CHROME_MIN_SCREEN_WIDTH / safeZoom;
  const outerWidth = Math.max(pageWidth, minWorldWidth);
  const innerWidth = Math.max(pageWidth * safeZoom, PAGE_CHROME_MIN_SCREEN_WIDTH);
  const chromeWorldHeight = pageChromeWorldHeight(safeZoom);

  const stopBubble = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  /** 点击标题栏空白（非 P{n} 拖柄、非按钮/输入框）→ 激活并显示页框选区 */
  const handleChromeBarClick = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('[data-page-drag-handle]')) return;
      if (el.closest('button, input, [role="button"]')) return;
      onChromeClick(e);
    },
    [onChromeClick],
  );

  const focusPage = useCallback(() => {
    if (!isActive) setCurrentPageId(page.id);
  }, [isActive, page.id, setCurrentPageId]);

  const commitName = useCallback(() => {
    const trimmed = nameDraft.trim();
    if (trimmed !== page.name.trim()) {
      setPageName(page.id, trimmed);
    } else {
      setNameDraft(page.name);
    }
  }, [nameDraft, page.id, page.name, setPageName]);

  const toastPageLimit = useCallback(() => {
    toast.warning(`文档最多支持 ${MAX_WORKSPACE_PAGES} 页，已达上限`);
  }, []);

  const handleDelete = useCallback(() => {
    deletePage(page.id);
  }, [page.id, deletePage]);

  const handleDuplicate = useCallback(() => {
    if (pageCount >= MAX_WORKSPACE_PAGES) {
      toastPageLimit();
      return;
    }
    if (!duplicatePage(page.id)) {
      toast.warning('复制页面失败，请重试');
    }
  }, [page.id, pageCount, duplicatePage, toastPageLimit]);

  const handleAddAfter = useCallback(() => {
    if (pageCount >= MAX_WORKSPACE_PAGES) {
      toastPageLimit();
      return;
    }
    if (!addPageAfter(page.id)) {
      toast.warning('添加页面失败，请重试');
    }
  }, [page.id, pageCount, addPageAfter, toastPageLimit]);

  const canGoPrev = canNavigateWorkspacePage('prev', pageIndex, pageCount);
  const canGoNext = canNavigateWorkspacePage('next', pageIndex, pageCount);

  const handleGoPrev = useCallback(() => {
    if (!canGoPrev) return;
    navigateWorkspacePage('prev', page.id);
  }, [canGoPrev, page.id]);

  const handleGoNext = useCallback(() => {
    if (!canGoNext) return;
    navigateWorkspacePage('next', page.id);
  }, [canGoNext, page.id]);

  const showPageNav = isActive && pageCount > 1;

  return (
    <div
      data-page-chrome
      className="shrink-0 overflow-visible"
      style={{ width: outerWidth, height: chromeWorldHeight }}
      onMouseDown={stopBubble}
      onClick={handleChromeBarClick}
    >
      <div
        className={cn(
          'box-border flex items-center justify-between gap-2',
          'bg-background',
          isActive ? 'text-default-foreground' : 'text-default-600',
        )}
        style={{
          width: innerWidth,
          minHeight: PAGE_CHROME_HEIGHT,
          transform: `scale(${1 / safeZoom})`,
          transformOrigin: 'top left',
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            data-page-drag-handle
            className={cn(
              'shrink-0 cursor-grab select-none text-xs font-semibold tabular-nums',
              isDragging && 'cursor-grabbing',
            )}
            onMouseDown={onDragHandleMouseDown}
            onClick={onChromeClick}
          >
            P{pageIndex}
          </span>
          <Input
            aria-label="页面名称"
            placeholder="添加页面名称"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onFocus={focusPage}
            onMouseDown={stopBubble}
            onClick={stopBubble}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            disabled={readonly}
            className="min-h-6 h-6 min-w-[2rem] max-w-[16rem] flex-1 text-xs bg-transparent shadow-none"
          />
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          {showPageNav ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                isIconOnly
                aria-label="上一页"
                isDisabled={!canGoPrev}
                onPress={handleGoPrev}
                onMouseDown={stopBubble}
              >
                <IconChevronUp
                  className={cn('size-4', pageChromeNavIconClass(workspacePageLayoutMode, 'prev'))}
                  stroke={1.25}
                />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                isIconOnly
                aria-label="下一页"
                isDisabled={!canGoNext}
                onPress={handleGoNext}
                onMouseDown={stopBubble}
              >
                <IconChevronDown
                  className={cn('size-4', pageChromeNavIconClass(workspacePageLayoutMode, 'next'))}
                  stroke={1.25}
                />
              </Button>
            </>
          ) : null}
          {!readonly ? (
            <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              isIconOnly
              aria-label="删除页面"
              onPress={handleDelete}
              onMouseDown={stopBubble}
            >
              <IconTrash stroke={1.25} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              isIconOnly
              aria-label="复制页面"
              onPress={handleDuplicate}
              onMouseDown={stopBubble}
            >
              <IconCopy stroke={1.25} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              isIconOnly
              aria-label="在后面添加页面"
              onPress={handleAddAfter}
              onMouseDown={stopBubble}
            >
              <IconPlus stroke={1.25} />
            </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
